// Background-safe skip-dose path used by the iOS notification action.
//
// When the user taps "Skip" on a dose-reminder notification, we want
// the skip to register WITHOUT bringing the app forward (foreground:
// false on the action). That means the work has to happen in the
// notification listener, outside any React component. This module is
// the React-free equivalent of HomePage's skipDoseParam handler:
// fetch protocols + today's logs, find the pending dose, write the
// skipped DoseLog row(s).
//
// v3 (E9): schedules come from generateProtocolSchedule, so a
// combined-vial reminder resolves to ONE group dose; skipping it
// writes one skipped row per component sharing one administrationId
// via the shared skipScheduledDose helper.

import { fetchActiveProtocols } from './cloud/protocols';
import { fetchTodayDoseLogs } from './cloud/doseLogs';
import { generateProtocolSchedule } from '../utils/schedule';
import { skipScheduledDose } from './groupDosing';

export type SkipResult =
  | { ok: true; peptideName: string }
  | { ok: false; reason: 'not_found' | 'already_logged' | 'error'; message?: string };

/**
 * Find today's pending dose for the given component and write the
 * skipped DoseLog row(s). Mirrors HomePage's skip-from-notification
 * logic but doesn't depend on any React state.
 *
 * The componentId comes from the notification payload. For combined
 * groups the payload carries the group's lead componentId, but we
 * also match group members defensively so a stale payload still
 * resolves to the right administration.
 *
 * Called from the notification action listener in
 * useNotificationActions.ts when the user taps Skip with the action
 * configured as foreground: false (so the app stays backgrounded).
 */
export async function skipDoseFromNotification(
  componentId: string,
): Promise<SkipResult> {
  try {
    const today = new Date();
    const [protocols, todayLogs] = await Promise.all([
      fetchActiveProtocols(),
      fetchTodayDoseLogs(),
    ]);

    // Locate the protocol this notification was scheduled for.
    const matchedProtocol = protocols.find((p) =>
      p.components.some((c) => c.id === componentId),
    );
    if (!matchedProtocol) {
      return { ok: false, reason: 'not_found' };
    }

    // Generate today's schedule to find the pending administration.
    // If the user already logged it (manually, before tapping Skip),
    // bail rather than double-recording.
    const schedule = generateProtocolSchedule(matchedProtocol, today, todayLogs);
    const pending = schedule.find(
      (s) =>
        !s.isCompleted &&
        (s.componentId === componentId ||
          s.groupComponents?.some((gc) => gc.componentId === componentId)),
    );
    if (!pending) {
      return { ok: false, reason: 'already_logged' };
    }

    const [skipHour, skipMin] = pending.scheduledTime.split(':').map(Number);
    const loggedTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      skipHour,
      skipMin,
      0,
      0,
    ).toISOString();

    await skipScheduledDose(pending, loggedTime, 'Skipped from notification');

    return { ok: true, peptideName: pending.peptideName };
  } catch (err) {
    console.error('[skipDoseFromNotification] failed:', err);
    return {
      ok: false,
      reason: 'error',
      message: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}
